import { Dispatch, SetStateAction, useEffect, useMemo, useState } from 'react';
import { css } from '@emotion/react';
import { Direction_Enum, Status_Enum } from '@weave/schema-gen-ts/dist/schemas/fax/shared/v1/enums.pb';
import { UpdateSummaryFeedbackRequest } from '@weave/schema-gen-ts/dist/schemas/fax/summary/v1/fax_summary.pb';
import { Fax, FaxNumber } from '@weave/schema-gen-ts/dist/schemas/fax/v1/fax.pb';
import { IdentifierType } from '@weave/schema-gen-ts/dist/schemas/notification/v1/notification_api.pb';
import type { Tag } from '@weave/schema-gen-ts/dist/schemas/tag/shared/v1/models.pb';
import { NotificationType } from '@weave/schema-gen-ts/dist/shared/notification/notifications.pb';
import { Permission } from '@weave/schema-gen-ts/dist/shared/waccess/acls.pb';
import { FaxQueries, FaxTypes, FaxV1 } from '@frontend/api-fax';
import { NotificationQueries } from '@frontend/api-notifications';
import { hasSchemaACL } from '@frontend/auth-helpers';
import { useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { InboxLocationPhones } from '@frontend/inbox-location-phones';
import { formatPhoneNumberE164 } from '@frontend/phone-numbers';
import { SchemaFaxService } from '@frontend/schema';
import { useAppScopeStore, useScopedAppFlagStore } from '@frontend/scope';
import { useSettingsNavigate } from '@frontend/settings-routing';
import { useSlidePanelShallowStore } from '@frontend/slide-panel';
import { TagSelectionHooks } from '@frontend/tag-selection';
import { FaxPrefixes } from '@frontend/tracking-prefixes';
import { ComponentProps } from '@frontend/types';
import { useLocation, useNavigate } from '@frontend/weave-router';
import { theme } from '@frontend/theme';
import { Chip, ListRow, phone, Text, useModalControl, usePopoverMenu, useTooltip } from '@frontend/design-system';
import { useFaxActions } from '../../hooks/use-fax-actions';
import { useDeleteConfirmationShallowStore } from '../../stores/delete-confirmation-store';
import { useSelectedFaxShallowStore } from '../../stores/selected-fax-store';
import { getFaxIdFromURL } from '../../utils/get-fax-id-from-url';
import { DeleteFaxConfirmationModal } from './delete-fax-confirmation-modal';
import { FaxAiSummaryPopover } from './fax-ai-summary-popover/fax-ai-summary-popover';
import { FeedbackModal } from './fax-ai-summary-popover/feedback-modal';
import { FaxInboxListItemLead } from './fax-inbox-list-item-lead';
import { FaxListRow, FloatingFaxListRow } from './fax-list-row';
import { ForwardFaxModal } from './forward-fax-modal';
import { InboxListItemTags } from './list-inbox-item-tags';

type RendererProps = {
  type: FaxTypes.InboxType;
  isBulkSelect: boolean;
  selectedFaxes: Record<string, string[]>;
  setSelectedFaxes: Dispatch<SetStateAction<Record<string, string[]>>>;
  isSelectAll: boolean;
  locationTags: Tag[];
  isBlocked: boolean;
  listItem: Fax;
  faxNumbers: FaxNumber[];
  isCompactMode?: boolean;
};

export const InboxListItem = ({
  type,
  isBulkSelect,
  selectedFaxes,
  setSelectedFaxes,
  isSelectAll,
  locationTags,
  isBlocked = false,
  listItem,
  faxNumbers,
  isCompactMode,
}: RendererProps) => {
  const { locationId, id: faxId } = listItem;
  const { t } = useTranslation('fax');
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { modalProps: forwardModalProps, openModal } = useModalControl();
  const { modalProps: feedbackModalProps, openModal: openFeedbackModal } = useModalControl();
  const { modalProps: deleteModalProps, openModal: openDeleteModal } = useModalControl();
  const { navigate: settingsNavigate } = useSettingsNavigate();
  const { selectedFax, setSelectedFax } = useSelectedFaxShallowStore('selectedFax', 'setSelectedFax');
  const { getScopeName, selectedLocationIds } = useAppScopeStore();
  const { updateFaxReadStatusInAllCaches } = FaxV1.FaxQueryUpdaters.useListFaxQueryUpdater();
  const { previewFax, downloadFax, markReadFax, deleteFax, blockFax, archiveFax, tagFax, unblockFax, retryFax } =
    useFaxActions();
  const { skipFaxDeleteConfirmation } = useDeleteConfirmationShallowStore('skipFaxDeleteConfirmation');
  const { setShow } = useSlidePanelShallowStore('setShow');
  const { getFeatureFlagValue } = useScopedAppFlagStore();
  const enableFaxDeliveryReport = getFeatureFlagValue('fax-delivery-report');
  const faxSummaryFlag = getFeatureFlagValue('fax-summary');
  const faxDeleteAcl = hasSchemaACL(locationId, Permission.FAX_DELETE);
  const [profileMedia, setProfileMedia] = useState('');
  const [tagSearchValue, setTagSearchValue] = useState<string>('');
  const [isSummaryLoading, setIsSummaryLoading] = useState(false);
  const [hasSummary, setHasSummary] = useState(false);
  const markAsReadMutation = NotificationQueries.useMarkNotificationsAsRead();
  const [isRetrying, setIsRetrying] = useState(false);
  const [feedbackRequest, setFeedbackRequest] = useState<UpdateSummaryFeedbackRequest | undefined>(undefined);

  // Actions popover for the list row
  const actionsPopover = usePopoverMenu({
    placement: 'bottom-start',
    middlewareOptions: {
      offset: {
        alignmentAxis: 0,
        mainAxis: 0,
      },
    },
  });

  const aiSummaryPopover = usePopoverMenu<HTMLDivElement>({
    placement: isCompactMode ? 'bottom-end' : 'right-start',
    middlewareOptions: {
      shift: {
        padding: 12,
      },
    },
    interactionOptions: {
      hover: {
        enabled: true,
        delay: {
          open: 100,
          close: 100,
        },
        move: true,
      },
      click: {
        enabled: false,
      },
    },
  });

  const {
    Tooltip,
    triggerProps: tooltipTriggerProps,
    tooltipProps,
  } = useTooltip({
    trigger: 'hover',
    placement: 'left-start',
    offset: Number(theme.spacing(1).replace('px', '')),
  });

  const {
    Tooltip: LocationTooltip,
    triggerProps: locationTooltipTriggerProps,
    tooltipProps: locationTooltipProps,
  } = useTooltip({
    trigger: 'hover',
    placement: 'bottom',
    offset: Number(theme.spacing(1).replace('px', '')),
  });

  const {
    Tooltip: FaxNumberTooltip,
    triggerProps: faxNumberTooltipTriggerProps,
    tooltipProps: faxNumberTooltipProps,
  } = useTooltip({
    trigger: 'hover',
    placement: 'bottom',
    offset: Number(theme.spacing(1).replace('px', '')),
  });

  const checkUserLocationPhoneAccess = InboxLocationPhones.Hooks.useCheckUserLocationPhoneAccess();

  const getHoverStatusMessage = (status: FaxTypes.FaxStatus): string => {
    switch (status) {
      case FaxTypes.FaxStatus.UNREAD:
        return t('Unread');
      case FaxTypes.FaxStatus.READ:
        return t('Read');
      case FaxTypes.FaxStatus.OUTGOING_SENT:
        return t('Sent');
      case FaxTypes.FaxStatus.DELIVERY_SUCCESS:
        return t('Delivered');
      case FaxTypes.FaxStatus.ERROR:
        return t('Error');
      case FaxTypes.FaxStatus.DRAFT:
        return t('Draft');
      case FaxTypes.FaxStatus.RETRYING:
        return t('Retrying');
      case FaxTypes.FaxStatus.BLOCKED:
        return t('Blocked');
      case FaxTypes.FaxStatus.ARCHIVED:
        return t('Archived');
    }
  };

  const { data: blob } = FaxQueries.useGetFaxContactMedia(listItem.faxContact?.profileMediaId ?? '', locationId, {
    enabled: !!listItem.faxContact?.profileMediaId,
  });

  const selectedFaxId = getFaxIdFromURL(pathname ?? '', type);

  const isSelectedFax = selectedFaxId ? selectedFaxId === faxId : false;

  const previewPopover = usePopoverMenu<HTMLButtonElement>({
    placement: 'right-start',
    interactionOptions: {
      hover: {
        enabled: true,
      },
    },
  });

  const downloadPopover = usePopoverMenu<HTMLButtonElement>({
    placement: 'right-start',
    interactionOptions: {
      hover: {
        enabled: true,
      },
    },
  });

  const tags = locationTags.filter((tag) => listItem.tags?.includes(tag.id || tag.smartTagId));

  const tagIdsToExclude = tags.map((tag) => tag.id) ?? [];

  const addTag = async (tag: Tag) => {
    const tagId = tag.id || tag.smartTagId;
    try {
      await tagFax(locationId, [faxId], [tagId]);
      if (selectedFax.faxId === faxId) {
        setSelectedFax({
          ...selectedFax,
          tags: [...selectedFax.tags, tagId],
        });
      }
    } catch (error) {
      console.error('Error tagging fax', error);
    }
  };

  const {
    popoverProps: tagPopoverProps,
    open: openTagSelection,
    isOpen: tagSelectionIsOpen,
    close: closeTagSelection,
    getTriggerProps: getTagSelectionTriggerProps,
  } = TagSelectionHooks.useTagSelectionPopover({
    onClose: () => {
      setTagSearchValue('');
      closeTagSelection();
    },
    onTagSelect: async (tag) => {
      addTag(tag);
      closeTagSelection();
    },
    onTagCreate: (initName) => {
      settingsNavigate({
        to: '/tags/quick-create',
        context: {
          onCreate: (tag: Tag) => {
            addTag(tag);
          },
          name: initName,
        },
      });
    },
    trackingIds: {
      selectTag: `${FaxPrefixes.Tag}-add-existing-tag`,
      createTag: `${FaxPrefixes.Tag}-create-new-tag`,
      createNamedTag: `${FaxPrefixes.Tag}-create-new-tag-with-name`,
      openTagSettings: `${FaxPrefixes.Tag}-open-tag-settings`,
    },
    searchValue: tagSearchValue,
    tagIdsToExclude,
    placement: 'right-start',
  });

  const contactPhone = listItem.contactPhone;

  const formattedPhone = contactPhone === 'Unknown' ? t('Unknown') : phone(contactPhone ?? '', { hideCountry: true });
  const isRead = !!listItem?.viewedAt;
  const isInbound = listItem.direction === Direction_Enum.INBOUND;
  const isOutbound = listItem.direction === Direction_Enum.OUTBOUND;

  const faxNumberDisplayName = faxNumbers.find(
    (num) => num.locationPhone.replace(/^\+\d/, '') === listItem.locationPhone
  )?.displayName;
  const formattedFaxNumber = `# ${phone(listItem.locationPhone)}`;
  const faxNumberDisplay = faxNumberDisplayName ? `# ${faxNumberDisplayName}` : formattedFaxNumber;
  const faxNumberHoverLabel = faxNumberDisplayName ? (
    <>
      {faxNumberDisplayName}
      <br />
      {phone(listItem.locationPhone)}
    </>
  ) : (
    phone(listItem.locationPhone)
  );

  const personName = `${listItem.faxContact?.name} ${listItem.faxContact?.secondName ?? ''}`;

  const title = personName.trim() || formattedPhone;

  const getStatus = (): FaxTypes.FaxStatus => {
    if (listItem.status === Status_Enum.FAILED_FATAL || listItem.status === Status_Enum.FAILED_RETRYABLE)
      return FaxTypes.FaxStatus.ERROR;
    if (listItem.status === Status_Enum.RETRYING) return FaxTypes.FaxStatus.RETRYING;
    if (isBlocked) return FaxTypes.FaxStatus.BLOCKED;
    if (!!listItem.archivedAt) return FaxTypes.FaxStatus.ARCHIVED;
    if (listItem.direction === Direction_Enum.OUTBOUND && listItem.status === Status_Enum.DELIVERED)
      return FaxTypes.FaxStatus.DELIVERY_SUCCESS;
    if (listItem.direction === Direction_Enum.OUTBOUND) return FaxTypes.FaxStatus.OUTGOING_SENT;
    if (listItem.direction === Direction_Enum.INBOUND && !isRead) return FaxTypes.FaxStatus.UNREAD;
    return FaxTypes.FaxStatus.READ;
  };

  const isCheckboxSelected = isSelectAll
    ? !selectedFaxes[locationId]?.includes(faxId) && isInbound
    : !!selectedFaxes[locationId]?.includes(faxId) && isInbound;

  const setCheckboxSelected = (selected: boolean) => {
    let faxes: string[] = [];
    const filteredFaxes = selectedFaxes[locationId]?.filter((id) => id !== faxId) ?? [];
    const updatedFaxes = !selectedFaxes[locationId]?.includes(faxId)
      ? [...(selectedFaxes[locationId] ?? []), faxId]
      : selectedFaxes[locationId];

    if (isSelectAll) {
      faxes = selected ? filteredFaxes : updatedFaxes;
    } else {
      faxes = selected ? updatedFaxes : filteredFaxes;
    }

    setSelectedFaxes((prev) => ({ ...prev, [locationId]: faxes }));
  };

  const handleRetry = async () => {
    if (isRetrying) return;
    setIsRetrying(true);
    try {
      await retryFax(faxId, locationId);
    } finally {
      setIsRetrying(false);
    }
  };

  const archiveButtonProps: ComponentProps<typeof FaxListRow>['archiveButtonProps'] =
    listItem.status === Status_Enum.FAILED_RETRYABLE
      ? {
          iconName: 'update',
          label: t('Retry'),
          onClick: handleRetry,
          trackingId: `${FaxPrefixes.List}-retry-button`,
        }
      : {
          label: type === 'archived' ? t('Unarchive') : t('Archive'),
          iconName: type === 'archived' ? 'unarchive' : 'archive',
          onClick: () => archiveFax([faxId], locationId, !listItem.archivedAt),
          trackingId:
            type === 'archived' ? `${FaxPrefixes.Archived}-unarchive-button` : `${FaxPrefixes.List}-archive-button`,
        };

  const threadInfo = useMemo(
    () => ({
      faxId: faxId ?? '',
      mediaId: listItem.mediaId ?? '',
      contactName: personName.trim(),
      contactPhone: contactPhone ?? '',
      contactId: listItem.faxContact?.contactId ?? '',
      profileMediaId: listItem.faxContact?.profileMediaId ?? '',
      profileMedia: profileMedia ?? '',
      locationId: locationId ?? '',
      locationPhone: listItem.locationPhone ?? '',
      locationPhoneDisplayName: faxNumberDisplayName ?? '',
      direction: listItem.direction,
      provider: listItem.provider,
      status: listItem.status ?? '',
      isArchived: !!listItem.archivedAt,
      isBlocked,
      tags: listItem.tags ?? [],
      thumbnailIds: [],
      pages: 0,
      isThumbnailsLoading: false,
    }),
    [
      faxId,
      listItem.mediaId,
      listItem.faxContact?.contactId,
      listItem.faxContact?.profileMediaId,
      listItem.direction,
      listItem.provider,
      listItem.status,
      listItem.archivedAt,
      listItem.tags,
      personName,
      contactPhone,
      profileMedia,
      locationId,
      isBlocked,
      faxNumberDisplayName,
    ]
  );

  useEffect(() => {
    if (selectedFax.faxId !== faxId && isSelectedFax) {
      setSelectedFax(threadInfo);
    }
  }, [isSelectedFax, faxId, selectedFax.faxId]);

  useEffect(() => {
    if (selectedFax.faxId !== faxId || !isSelectedFax) {
      return;
    }

    const hasPropertyChanges =
      threadInfo.contactName !== selectedFax.contactName ||
      threadInfo.profileMediaId !== selectedFax.profileMediaId ||
      threadInfo.profileMedia !== selectedFax.profileMedia;
    if (hasPropertyChanges) {
      setSelectedFax({
        ...selectedFax,
        contactName: threadInfo.contactName,
        profileMediaId: threadInfo.profileMediaId,
        profileMedia: threadInfo.profileMedia,
      });
      return;
    }
  }, [isSelectedFax, faxId, threadInfo.contactName, threadInfo.profileMedia, threadInfo.profileMediaId]);

  const actions = [
    ...(enableFaxDeliveryReport && isOutbound && listItem.status === Status_Enum.DELIVERED
      ? [
          {
            actionIcon: () => <Icon name='preview' size={16} css={{ color: theme.colors.neutral70 }} />,
            label: t('Preview / Print'),
            action: () => previewFax(faxId ?? '', `${listItem.contactPhone}-fax-preview`, locationId, true),
            trackingId: `${FaxPrefixes.List}-preview-button`,
            disableCloseOnSelect: true,
            popoverProps: previewPopover,
            locationId,
            faxId,
            order: 0,
          },
          {
            actionIcon: () => <Icon name='download' size={16} css={{ color: theme.colors.neutral70 }} />,
            label: t('Download'),
            action: () => downloadFax(faxId ?? '', `${listItem.contactPhone}-fax-download`, locationId, true),
            trackingId: `${FaxPrefixes.List}-download-button`,
            disableCloseOnSelect: true,
            popoverProps: downloadPopover,
            faxId,
            locationId,
            order: 1,
          },
        ]
      : [
          {
            actionIcon: () => <Icon name='preview' size={16} css={{ color: theme.colors.neutral70 }} />,
            label: t('Preview / Print'),
            action: () => {
              previewFax(faxId ?? '', `${listItem.contactPhone}-fax-preview`, locationId, true);
              if (!isRead) {
                updateFaxReadStatusInAllCaches(faxId, true);
              }
            },
            trackingId: `${FaxPrefixes.List}-preview-button`,
          },
          {
            actionIcon: () => <Icon name='download' size={16} css={{ color: theme.colors.neutral70 }} />,
            label: t('Download'),
            action: () => {
              downloadFax(faxId ?? '', `${listItem.contactPhone}-fax-download`, locationId, true);
              if (!isRead) {
                updateFaxReadStatusInAllCaches(faxId, true);
              }
            },
            trackingId: `${FaxPrefixes.List}-download-button`,
          },
        ]),
    {
      actionIcon: () => <Icon name='forward' size={16} css={{ color: theme.colors.neutral70 }} />,
      label: t('Forward'),
      action: () => {
        if (!selectedFax.faxId) {
          setSelectedFax(threadInfo);
        }
        openModal();
      },
      disabled:
        /*
          We need to disable the forward button if the user does not have access to the To: fax number in case of inbound faxes and From: fax number in case of outbound faxes.
          Both these correspond to the locationPhone which is what has been used in the check.
        */
        !!threadInfo.locationPhone && !checkUserLocationPhoneAccess(formatPhoneNumberE164(threadInfo.locationPhone)),
      disabledTooltipText: t('You do not have access to this fax number'),
      trackingId: `${FaxPrefixes.List}-forward-button`,
    },
    ...(type !== FaxTypes.InboxType.DRAFTS && !listItem.faxContact?.contactId
      ? [
          {
            actionIcon: () => <Icon name='user' size={16} css={{ color: theme.colors.neutral70 }} />,
            label: t('Add Contact'),
            action: () =>
              setShow(true, 'faxContactForm', {
                contact: {
                  name: '',
                  secondName: '',
                  locationId: locationId,
                  faxNumber: listItem.contactPhone,
                },
                action: FaxTypes.ContactAction.ADD,
              }),
            trackingId: `${FaxPrefixes.List}-add-contact-button`,
          },
        ]
      : [
          {
            actionIcon: () => <Icon name='user' size={16} css={{ color: theme.colors.neutral70 }} />,
            label: t('Edit Contact'),
            action: () => {
              setShow(true, 'faxContactForm', {
                contactId: listItem.faxContact?.contactId ?? '',
                locationId: locationId,
                action: FaxTypes.ContactAction.EDIT,
              });
            },
            trackingId: `${FaxPrefixes.List}-edit-contact-button`,
          },
        ]),
    ...(type !== FaxTypes.InboxType.DRAFTS && isInbound
      ? [
          {
            actionIcon: isRead
              ? () => <Icon name='message-unread' size={16} css={{ color: theme.colors.neutral70 }} />
              : () => <Icon name='message' size={16} css={{ color: theme.colors.neutral70 }} />,
            label: isRead ? t('Mark Unread') : t('Mark Read'),
            action: () =>
              markReadFax(isRead, locationId, [faxId], listItem.locationPhone ? [listItem.locationPhone] : []),
            trackingId: `${FaxPrefixes.List}-mark-read-button`,
          },
        ]
      : []),
    {
      actionIcon: () => <Icon name='label' size={16} css={{ color: theme.colors.neutral70 }} />,
      label: t('Add Tag'),
      action: () => openTagSelection(),
      trackingId: `${FaxPrefixes.List}-tag-button`,
    },
    ...(faxDeleteAcl
      ? [
          {
            actionIcon: () => <Icon name='trash' size={16} css={{ color: theme.colors.neutral70 }} />,
            label: t('Delete'),
            action: () => {
              if (skipFaxDeleteConfirmation) {
                deleteFax(locationId, [faxId], type, listItem.locationPhone ? [listItem.locationPhone] : []);
              } else {
                openDeleteModal();
              }
            },
            trackingId: `${FaxPrefixes.List}-delete-button`,
          },
        ]
      : []),
    ...(type !== FaxTypes.InboxType.DRAFTS && isInbound && type !== FaxTypes.InboxType.BLOCKED
      ? [
          {
            actionIcon: () => <Icon name='block' size={16} css={{ color: theme.colors.neutral70 }} />,
            label: t('Block'),
            action: () => blockFax(locationId, contactPhone, faxId),
            trackingId: `${FaxPrefixes.List}-block-sender-button`,
          },
        ]
      : []),
    ...(type === FaxTypes.InboxType.BLOCKED
      ? [
          {
            actionIcon: () => <Icon name='block' size={16} css={{ color: theme.colors.neutral70 }} />,
            label: t('Unblock'),
            action: () => unblockFax(locationId, contactPhone, faxId),
            trackingId: `${FaxPrefixes.Blocked}-unblock-sender-button`,
          },
        ]
      : []),
  ];

  const status = getStatus();

  const allowAISummary = faxSummaryFlag && [FaxTypes.InboxType.INBOX, FaxTypes.InboxType.ARCHIVED].includes(type);
  const canShowAISummaryPopover = allowAISummary && !actionsPopover.isOpen && aiSummaryPopover.isOpen;

  const summaryButtonProps: ComponentProps<typeof FaxListRow>['summaryButtonProps'] = {
    label: t('Summary'),
    trackingId: `${FaxPrefixes.List}-summary-button`,
    allowAISummary,
    isLoading: isSummaryLoading,
    hasSummary: hasSummary,
    getPopoverTriggerProps: aiSummaryPopover.getTriggerProps,
  };

  useEffect(() => {
    if (blob) {
      const url = URL.createObjectURL(blob);
      setProfileMedia(url);
    } else {
      setProfileMedia('');
    }

    return () => {
      if (profileMedia) {
        URL.revokeObjectURL(profileMedia);
      }
    };
  }, [blob]);

  const RowVariantComponent = isCompactMode ? FloatingFaxListRow : FaxListRow;

  return (
    <div>
      <RowVariantComponent
        css={actionableListRowStyles}
        Lead={
          <FaxInboxListItemLead
            isBulkSelect={isBulkSelect}
            personName={type !== FaxTypes.InboxType.DRAFTS ? personName : t('Draft')}
            contactId={listItem.faxContact?.contactId}
            locationId={locationId}
            isCheckboxSelected={isCheckboxSelected}
            setCheckboxSelected={setCheckboxSelected}
            profileMedia={profileMedia ?? ''}
            isInbound={isInbound}
            status={type === FaxTypes.InboxType.DRAFTS ? FaxTypes.FaxStatus.DRAFT : status}
          />
        }
        actions={actions}
        actionsPopoverProps={actionsPopover}
        Title={
          <div
            css={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-start',
              minWidth: 0,
              gap: theme.spacing(1),
            }}
            {...tooltipTriggerProps}
          >
            {status === FaxTypes.FaxStatus.UNREAD && (
              <span
                css={{
                  width: 8,
                  height: 8,
                  minWidth: 8,
                  borderRadius: theme.borderRadius.full,
                  backgroundColor: theme.colors.primary50,
                  display: 'inline-block',
                  flexShrink: 0,
                }}
              />
            )}
            <ListRow.Content.Title
              css={{
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
              isBold={type !== FaxTypes.InboxType.DRAFTS && isInbound ? !isRead : false}
            >
              {type !== FaxTypes.InboxType.DRAFTS ? title : t('Draft')}
            </ListRow.Content.Title>
            <div
              css={{
                display: 'flex',
                alignItems: 'center',
                gap: theme.spacing(0.25),
                marginLeft: `-${theme.spacing(1.25)}`,
                padding: theme.spacing(0, 0.5),
              }}
              className='inbox-item-pages'
            >
              <Text
                as='span'
                css={{
                  margin: 0,
                  flexShrink: 0,
                }}
                color='subdued'
                size='medium'
              >
                {listItem.pages}
              </Text>
            </div>
            <Tooltip {...tooltipProps}>{getHoverStatusMessage(status)}</Tooltip>
          </div>
        }
        Content={
          <>
            <div
              css={{
                display: 'flex',
                alignItems: 'center',
                columnGap: theme.spacing(1),
              }}
            >
              {selectedLocationIds.length > 1 && (
                <>
                  {isCompactMode ? (
                    <span css={{ display: 'inline-flex' }} {...locationTooltipTriggerProps}>
                      <Chip.Location
                        hideTooltip
                        css={{
                          fontSize: theme.fontSize(12),
                          color: theme.colorsV2.black(60),
                          backgroundColor: theme.colorsV2.black(10),
                          paddingLeft: theme.spacing(0.5),
                          paddingRight: theme.spacing(0.5),
                          minWidth: 0,
                          '& span': { display: 'none' },
                        }}
                        className='inbox-item-chip'
                      />
                    </span>
                  ) : (
                    <Chip.Location
                      hideTooltip
                      css={{
                        fontSize: theme.fontSize(12),
                        color: theme.colorsV2.black(60),
                        backgroundColor: theme.colorsV2.black(10),
                      }}
                      className='inbox-item-chip'
                    >
                      {getScopeName(locationId)}
                    </Chip.Location>
                  )}
                  {isCompactMode && (
                    <LocationTooltip {...locationTooltipProps}>{getScopeName(locationId)}</LocationTooltip>
                  )}
                </>
              )}
              {faxNumbers.length > 1 && (
                <>
                  <Chip.Tag
                    variant='neutral'
                    className='inbox-item-chip'
                    css={{
                      fontSize: theme.fontSize(12),
                      color: theme.colorsV2.black(60),
                      backgroundColor: theme.colorsV2.black(10),
                      maxWidth: 180,
                    }}
                    {...faxNumberTooltipTriggerProps}
                  >
                    {isCompactMode ? '#' : faxNumberDisplay}
                  </Chip.Tag>
                  <FaxNumberTooltip {...faxNumberTooltipProps}>{faxNumberHoverLabel}</FaxNumberTooltip>
                </>
              )}
            </div>
            {type !== FaxTypes.InboxType.DRAFTS && (
              <InboxListItemTags
                tags={tags}
                locationId={locationId}
                faxId={faxId}
                tagSelectionIsOpen={tagSelectionIsOpen}
                tagPopoverProps={tagPopoverProps}
                getTagSelectionTriggerProps={getTagSelectionTriggerProps}
                closeTagSelection={closeTagSelection}
                setTagSearchValue={setTagSearchValue}
                tagSearchValue={tagSearchValue}
                addTag={addTag}
              />
            )}
          </>
        }
        archiveButtonProps={
          type !== FaxTypes.InboxType.DRAFTS &&
          (listItem.direction === Direction_Enum.INBOUND ||
            listItem.direction === Direction_Enum.OUTBOUND ||
            listItem.status === Status_Enum.FAILED_RETRYABLE)
            ? archiveButtonProps
            : undefined
        }
        summaryButtonProps={summaryButtonProps}
        timestamp={listItem.createdAt}
        tabIndex={0}
        key={faxId}
        id={faxId}
        onClick={async () => {
          aiSummaryPopover.close();
          navigate({
            to: type === FaxTypes.InboxType.DRAFTS ? `/fax/drafts_/${faxId}/cover` : `/fax/${type}/${faxId}`,
          });
          if (!isRead && isInbound) {
            try {
              //Mark notification as Read for all the users on the location
              markAsReadMutation.mutate({
                notificationType: NotificationType.FAX,
                identifiers: [
                  {
                    key: 'faxId',
                    val: faxId,
                    type: IdentifierType.STRING,
                  },
                ],
              });
              await SchemaFaxService.UpdateFaxes({
                hasBeenRead: !isRead,
                locationId,
                faxIds: [faxId],
                locationPhones: listItem.locationPhone ? [listItem.locationPhone] : [],
              });
            } catch (error) {
              console.error('Error updating fax', error);
            }
          }

          // TODO: Check and remove later.
          // if (
          //   selectedFax.faxId === faxId &&
          //   selectedFax.mediaId === listItem.mediaId &&
          //   selectedFax.thumbnailIds.length > 0
          // ) {
          //   return;
          // }

          // setSelectedFax(threadInfo);

          // const res = await generateMediaAttributes({ mediaId: listItem.mediaId ?? '', locationId });

          // setSelectedFax({
          //   ...threadInfo,
          //   pages: res.attributes.pages,
          //   thumbnailIds: res.attributes.largeThumbnails ?? [],
          // });
        }}
        isSelected={isSelectedFax}
        data-trackingid={`${FaxPrefixes.List}-inbox-item`}
      />
      {forwardModalProps.show && (
        <ForwardFaxModal forwardModalProps={forwardModalProps} locationId={locationId} faxId={faxId} />
      )}
      <DeleteFaxConfirmationModal
        modalProps={deleteModalProps}
        onDelete={() => deleteFax(locationId, [faxId], type, listItem.locationPhone ? [listItem.locationPhone] : [])}
        deleteType='fax'
      />
      {canShowAISummaryPopover && (
        <FaxAiSummaryPopover
          popoverProps={aiSummaryPopover}
          contactName={personName}
          contactNumber={formattedPhone}
          direction={listItem.direction}
          mediaId={listItem.mediaId ?? ''}
          faxId={faxId}
          locationId={locationId}
          openFeedbackModal={openFeedbackModal}
          setFeedbackRequest={setFeedbackRequest}
          onLoadingChange={setIsSummaryLoading}
          onLoadSuccess={setHasSummary}
        />
      )}
      {allowAISummary && (
        <FeedbackModal
          feedbackModalProps={feedbackModalProps}
          feedbackRequest={feedbackRequest}
          setFeedbackRequest={setFeedbackRequest}
        />
      )}
    </div>
  );
};

const actionableListRowStyles = css`
  padding: ${theme.spacing(2)};
  padding-left: ${theme.spacing(3)};
  overflow-x: hidden;

  &:hover .inbox-item-chip {
    background-color: ${theme.colors.white};
  }

  &:hover .inbox-item-pages {
    background-color: ${theme.colors.white};
    border-radius: ${theme.borderRadius.small};
  }
`;
